// @ts-strict-ignore
import {CustomProperty} from 'common/types/reports/query';
import {apiClient} from 'common/util/api-client';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {getLocation} from 'common/util/url';
import {ApiResourceClient} from 'common/util/model-resource/api-resource';

import {appApiUrl} from '../utils';
import {BaseClient} from '../utils/base-client';
import {purgeFetchCache} from '../utils/fetch-cache';

class CustomPropertyClientLegacy extends BaseClient {
  buildEndpoint({projectId, workspaceId, customPropertyId, validate}) {
    let endpoint = appApiUrl(`custom_properties/`, {projectId, workspaceId});
    if (customPropertyId) {
      endpoint += `${customPropertyId}`;
    } else if (validate) {
      endpoint += `validate`;
    }

    return endpoint;
  }

  buildRequestBody({customProperty}) {
    return JSON.stringify(customProperty);
  }

  processResponse(response) {
    return response;
  }
}

export const customPropertyClientLegacy = new CustomPropertyClientLegacy();

function buildEndpoint(customPropertyId?: number, validate?: boolean) {
  let endpoint = appApiUrl(`custom_properties/`, {projectId: getProjectId(), workspaceId: getWorkspaceId()});
  if (customPropertyId) {
    endpoint += `${customPropertyId}`;
  } else if (validate) {
    endpoint += `validate`;
  }

  return endpoint;
}

async function fetchCustomProperties(fromFetchCache: boolean): Promise<{[key: string]: CustomProperty}> {
  // eslint-disable-next-line local-rules/deprecated-member-access
  const result = await apiClient.fetch(buildEndpoint(), {
    headers: {
      'Request-URL': getLocation().href,
    },
    method: `GET`,
    fromFetchCache,
  });
  const customProperties: CustomProperty[] = (await result?.json())?.results ?? [];
  // If this is from cache, and it's empty, return null instead of an empty set.
  // Otherwise, ApiResource will signal that the index fetch is done, when it really isn't
  if (!customProperties.length && fromFetchCache) {
    return null;
  }
  return customProperties.reduce((acc, eventDefinition) => {
    acc[eventDefinition.customPropertyId] = eventDefinition;
    return acc;
  }, {});
}

/**
 * Bulk update operation for custom properties. Under the hood this uses `PUT` to modify the resource.
 *
 * @param customPropUpdates
 * @returns
 */
export async function bulkUpdate(
  customPropUpdates: Array<Partial<CustomProperty>>,
): Promise<Array<PromiseSettledResult<{error: Error | null; customPropertyId: string}>>> {
  const results = await Promise.allSettled(
    customPropUpdates.map(({customPropertyId, ...rest}) => {
      // note: the `PUT` verb is a lie: the API actually does a patch-style update as of 2025-02-04
      return apiClient
        .put(buildEndpoint(customPropertyId), rest)
        .then(() => {
          return Promise.resolve({error: null, customPropertyId: customPropertyId.toString()});
        })
        .catch((error) => {
          return Promise.reject(new Error(customPropertyId.toString(), {cause: error}));
        });
    }),
  );

  return results;
}

export async function bulkDelete(
  customPropertyIds: number[],
): Promise<Array<PromiseSettledResult<{error: Error | null; customPropertyId: string}>>> {
  const results = await Promise.allSettled(
    customPropertyIds.map((customPropertyId) => {
      return apiClient
        .delete(buildEndpoint(customPropertyId))
        .then(() => {
          return Promise.resolve({error: null, customPropertyId: customPropertyId.toString()});
        })
        .catch((error) => {
          return Promise.reject(new Error(customPropertyId.toString(), {cause: error}));
        });
    }),
  );

  return results;
}

export async function update(
  customPropertyId: number,
  updatePayload: Partial<CustomProperty>,
): Promise<CustomProperty> {
  return apiClient.put<CustomProperty>(buildEndpoint(customPropertyId), {
    ...updatePayload,
  });
}

export const customPropertyClient: ApiResourceClient<CustomProperty> = {
  index() {
    return {
      cache: fetchCustomProperties(true),
      network: fetchCustomProperties(false),
    };
  },

  get() {
    throw new Error(`Getting a single custom property is not implemented`);
  },

  purgeCache() {
    // NB: this purges any cached requests at the specified path, even if the query params are different
    purgeFetchCache({everything: true, path: buildEndpoint()});
  },
};
